import { makeStyles, Theme } from "@material-ui/core";

type Props = {
  background: string;
};

const useStyles = makeStyles<Theme, Props>((theme: Theme) => ({
  background: {
    backgroundColor: "#F8FAFC",
    minHeight: "100vh",
    paddingBottom: 60,
    position: "relative",
  },
  container: {
    maxWidth: 960,
    margin: "0 auto",
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
    },
  },
  headerBanner: {
    background: "linear-gradient(135deg, #0F172A 0%, #1E1B4B 100%)",
    borderRadius: 24,
    padding: "36px 40px",
    color: "#FFFFFF",
    marginTop: 32,
    marginBottom: 32,
    position: "relative",
    overflow: "hidden",
    boxShadow: "0 12px 32px rgba(15, 23, 42, 0.15)",
    [theme.breakpoints.down("sm")]: {
      padding: "24px 20px",
      borderRadius: 18,
    },
  },
  headerGlow: {
    position: "absolute",
    top: "-30%",
    right: "-10%",
    width: 300,
    height: 300,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(79, 70, 229, 0.35) 0%, rgba(0,0,0,0) 70%)",
    pointerEvents: "none",
  },
  headerBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(8px)",
    padding: "6px 14px",
    borderRadius: 100,
    fontSize: "0.8rem",
    fontWeight: 700,
    color: "#818CF8",
    letterSpacing: "0.05em",
    marginBottom: 12,
    border: "1px solid rgba(255, 255, 255, 0.15)",
  },
  headerTitle: {
    fontWeight: 900,
    fontSize: "1.75rem",
    letterSpacing: "-0.02em",
    marginBottom: 8,
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.35rem",
    },
  },
  headerSubtitle: {
    color: "#94A3B8",
    fontSize: "0.95rem",
    maxWidth: 640,
    lineHeight: 1.6,
  },

  registerCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 24,
    border: "1px solid #E2E8F0",
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
    padding: 40,
    [theme.breakpoints.down("sm")]: {
      padding: 20,
      borderRadius: 18,
    },
  },

  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 28,
    paddingBottom: 16,
    borderBottom: "2px solid #F1F5F9",
  },
  sectionTitle: {
    fontWeight: 800,
    fontSize: "1.2rem",
    color: "#0F172A",
  },

  formLabel: {
    fontWeight: 700,
    fontSize: "0.85rem",
    color: "#334155",
    marginBottom: 8,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  fieldGroup: {
    marginBottom: 20,
  },

  // Unified Form Controls Container
  formContainer: {
    "& .MuiFormControl-root": {
      width: "100%",
      margin: 0,
      padding: "0 !important",
    },

    // Override internal makeStyles padding on SelectField
    "& [class*='makeStyles-root']": {
      padding: "0 !important",
    },

    // 1. TextField / OutlinedInput styling (Height = 46px)
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px !important",
      backgroundColor: "#F8FAFC !important",
      height: "46px !important",
      boxSizing: "border-box !important",
      transition: "all 0.2s ease",
      "& fieldset": {
        borderColor: "#CBD5E1 !important",
        borderWidth: "1.5px !important",
        borderRadius: "12px !important",
      },
      "&:hover fieldset": {
        borderColor: "#94A3B8 !important",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#4F46E5 !important",
        borderWidth: "2px !important",
      },
    },

    // 2. SelectField styling (Height = 46px)
    "& div.MuiInputBase-root:not(.MuiOutlinedInput-root)": {
      borderRadius: "12px !important",
      backgroundColor: "#F8FAFC !important",
      border: "1.5px solid #CBD5E1 !important",
      height: "46px !important",
      transition: "all 0.2s ease",
      boxSizing: "border-box !important",
      padding: "0 !important",
      margin: "0 !important",
      display: "flex !important",
      alignItems: "center !important",
      "&:hover": {
        borderColor: "#94A3B8 !important",
      },
      "&:focus-within": {
        borderColor: "#4F46E5 !important",
        backgroundColor: "#FFFFFF !important",
        boxShadow: "0 0 0 3px rgba(79, 70, 229, 0.15)",
      },
      "&:before, &:after": {
        display: "none !important",
      },
    },

    // Inputs & Select internal text padding & alignment
    "& .MuiInputBase-input": {
      padding: "10px 16px !important",
      fontSize: "0.925rem",
      color: "#0F172A",
      fontWeight: 600,
      height: "100% !important",
      boxSizing: "border-box !important",
      display: "flex !important",
      alignItems: "center !important",
    },
    "& .MuiSelect-select.MuiSelect-select": {
      padding: "10px 32px 10px 16px !important",
      fontSize: "0.925rem",
      color: "#0F172A",
      fontWeight: 600,
      backgroundColor: "transparent !important",
      borderRadius: "12px !important",
      display: "flex !important",
      alignItems: "center !important",
      height: "100% !important",
      boxSizing: "border-box !important",
    },

    "& .MuiSelect-icon": {
      right: 12,
      color: "#64748B",
    },
    "& .MuiInputLabel-root": {
      display: "none !important",
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 4,
      marginTop: 4,
      fontSize: "0.75rem",
      fontWeight: 600,
    },
  },

  btnSubmit: {
    backgroundColor: "#4F46E5",
    color: "#FFFFFF",
    fontWeight: 800,
    fontSize: "1rem",
    borderRadius: 12,
    padding: "12px 36px",
    textTransform: "none",
    boxShadow: "0 6px 18px rgba(79, 70, 229, 0.25)",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#4338CA",
      boxShadow: "0 8px 24px rgba(79, 70, 229, 0.35)",
    },
  },

  loginPrompt: {
    marginTop: 24,
    textAlign: "center",
    fontSize: "0.925rem",
    color: "#64748B",
    "& span": {
      color: "#4F46E5",
      fontWeight: 800,
      cursor: "pointer",
      marginLeft: 6,
      "&:hover": {
        textDecoration: "underline",
      },
    },
  },

  // Legacy fallback props
  logo: {},
  content: {},
  form: {},
  form__title: {},
  form__content: {},
  btn_submit: {},
  root: {},
  error: {},
  label: {},
  options: {},
}));

export default useStyles;
